import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Card, Select, SearchField } from '@dreamtree-org/twreact-ui';
import { Search, ArrowRight, ArrowLeft, Lock } from 'lucide-react';
import { PageHeader, QueryState } from '@/components';
import { useQuery } from '@/lib/useQuery';
import { STAGES, STAGE_LABEL, nextStages, canTransition } from '../domain';
import { PriorityBadge, SlaBadge, useAction, ActionResult } from '../components';

const ORDER = Object.fromEntries(STAGES.map((s, i) => [s.key, i]));

function JobCard({ job, onOpen, onMove, busy }) {
  const moves = nextStages(job.stage).map((to) => ({ to, ...canTransition(job, to) }));
  const blocked = moves.find((m) => !m.ok && ORDER[m.to] > ORDER[job.stage]);
  return (
    <Card className="p-3 space-y-2">
      <button type="button" onClick={onOpen} className="w-full text-left space-y-1 min-h-[44px]">
        <div className="flex items-start justify-between gap-2">
          <span className="font-semibold">{job.id}</span>
          <span className="flex flex-wrap justify-end gap-1">
            {job.priority !== 'normal' ? <PriorityBadge priority={job.priority} /> : null}
            {job.sla !== 'on_track' && job.sla !== 'done' ? <SlaBadge sla={job.sla} /> : null}
          </span>
        </div>
        <div className="text-sm font-medium truncate">{job.device.model}</div>
        <div className="text-sm opacity-80 truncate">{job.problem}</div>
        <div className="text-xs opacity-70 truncate">
          {job.customer.name} · {job.technicianName}
        </div>
      </button>
      {moves.length ? (
        <div className="flex flex-wrap gap-1 pt-1 border-t border-[var(--app-border)]">
          {moves.map((m) => {
            const back = ORDER[m.to] < ORDER[job.stage];
            return (
              <Button
                key={m.to}
                size="sm"
                variant={back ? 'ghost' : 'outline'}
                disabled={!m.ok || busy}
                onClick={() => onMove(job, m.to)}
                title={m.ok ? `Move to ${STAGE_LABEL[m.to]}` : m.reason}
                leftIcon={back ? <ArrowLeft size={14} /> : null}
                rightIcon={back ? null : <ArrowRight size={14} />}
              >
                {STAGE_LABEL[m.to]}
              </Button>
            );
          })}
        </div>
      ) : null}
      {blocked ? (
        <p className="text-xs opacity-80 flex items-start gap-1">
          <Lock size={12} className="mt-0.5 shrink-0" aria-hidden /> {blocked.reason}
        </p>
      ) : null}
    </Card>
  );
}

/** SERV-03 Repair workbench: a Kanban of work orders by stage, moved only along allowed transitions. */
export default function Workbench() {
  const nav = useNavigate();
  const [tech, setTech] = useState('');
  const [search, setSearch] = useState('');
  const q = useQuery('serviceJobs', { technicianId: tech || undefined, q: search }, { select: (r) => r.data });
  const techs = useQuery('serviceTechnicians', null, { select: (r) => r.data });
  const act = useAction();

  const move = async (job, to) => {
    const res = await act.run('serviceJobMove', { id: job.id, to }, `${job.id} moved to ${STAGE_LABEL[to]}.`, job.id);
    if (res) q.reload();
  };

  return (
    <div className="space-y-4">
      <PageHeader title="Repair workbench" subtitle="Move a job only when its stage gate is met: approved quote before repair, all QC checks before return." />
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_16rem]">
        <SearchField
          placeholder="Search job, customer, model or problem"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          leftIcon={<Search size={16} />}
          aria-label="Search jobs" />
        <Select
          options={[{ value: '', label: 'All technicians' }, ...(techs.data || []).map((t) => ({ value: t.id, label: t.name }))]}
          value={tech}
          onChange={(v) => setTech(v || '')}
          closeOnSelect
          aria-label="Filter by technician"
        />
      </div>
      <ActionResult action={act} />
      <QueryState query={q}>
        {(jobs) => (
          <div className="-mx-3 sm:mx-0 overflow-x-auto snap-x snap-mandatory pb-2" role="region" aria-label="Jobs by stage">
            <div className="flex gap-3 px-3 sm:px-0 min-w-max">
              {STAGES.map((s) => {
                const col = jobs.filter((j) => j.stage === s.key);
                return (
                  <section key={s.key} className="snap-start w-[85vw] sm:w-72 shrink-0 rounded-lg bg-[var(--app-bg)] border border-[var(--app-border)] p-2 space-y-2" aria-label={s.label}>
                    <header className="flex items-center justify-between px-1 py-1">
                      <h2 className="font-semibold text-sm">{s.label}</h2>
                      <span className="text-xs rounded-full bg-[var(--app-surface)] border border-[var(--app-border)] px-2 py-0.5 tabular-nums">{col.length}</span>
                    </header>
                    {col.length === 0 ? <p className="text-xs opacity-60 px-1 py-4 text-center">No jobs</p> : null}
                    {col.map((j) => (
                      <JobCard key={j.id} job={j} onOpen={() => nav(`/jobs/${j.id}`)} onMove={move} busy={act.busy === j.id} />
                    ))}
                  </section>
                );
              })}
            </div>
          </div>
        )}
      </QueryState>
    </div>
  );
}
